<script setup>
import {
  RangeCalendarRoot,
  RangeCalendarHeader,
  RangeCalendarHeading,
  RangeCalendarPrev,
  RangeCalendarNext,
  RangeCalendarGrid,
  RangeCalendarGridHead,
  RangeCalendarGridBody,
  RangeCalendarGridRow,
  RangeCalendarHeadCell,
  RangeCalendarCell,
  RangeCalendarCellTrigger,
} from "reka-ui";
import { cn } from "@/shared/lib/utils";
import { buttonVariants } from "@/shared/ui/button";
import { weekdayLabels, monthYearLabel } from "./locale-labels";

const props = defineProps({
  modelValue: { type: Object, default: undefined },
  placeholder: { type: Object, default: undefined },
  locale: { type: String, default: "ru" },
  minValue: { type: Object, default: undefined },
  class: { type: String, default: "" },
});

const emit = defineEmits(["update:modelValue", "update:placeholder"]);
</script>

<template>
  <RangeCalendarRoot
    v-slot="{ grid }"
    :model-value="props.modelValue"
    :placeholder="props.placeholder"
    :locale="props.locale"
    :min-value="props.minValue"
    :week-starts-on="1"
    :class="cn('p-3', props.class)"
    @update:model-value="emit('update:modelValue', $event)"
    @update:placeholder="emit('update:placeholder', $event)"
  >
    <RangeCalendarHeader class="flex items-center justify-between">
      <RangeCalendarPrev
        :class="
          cn(
            buttonVariants({ variant: 'outline' }),
            'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100'
          )
        "
      >
        <svg-icon name="huge-chevron-left" class="w-4 h-4" />
      </RangeCalendarPrev>
      <RangeCalendarHeading class="text-sm font-medium">
        {{
          monthYearLabel(
            props.locale,
            grid?.[0]?.value?.month,
            grid?.[0]?.value?.year
          )
        }}
      </RangeCalendarHeading>
      <RangeCalendarNext
        :class="
          cn(
            buttonVariants({ variant: 'outline' }),
            'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100'
          )
        "
      >
        <svg-icon name="huge-chevron-right" class="w-4 h-4" />
      </RangeCalendarNext>
    </RangeCalendarHeader>

    <div class="flex flex-col gap-y-4 mt-4 sm:flex-row sm:gap-x-4 sm:gap-y-0">
      <RangeCalendarGrid
        v-for="month in grid"
        :key="month.value.toString()"
        class="w-full border-collapse"
      >
        <RangeCalendarGridHead>
          <RangeCalendarGridRow class="flex gap-1">
            <RangeCalendarHeadCell
              v-for="(day, i) in weekdayLabels(props.locale)"
              :key="i"
              class="w-7 rounded-md text-[0.7rem] font-normal text-muted-foreground"
            >
              {{ day }}
            </RangeCalendarHeadCell>
          </RangeCalendarGridRow>
        </RangeCalendarGridHead>
        <RangeCalendarGridBody>
          <RangeCalendarGridRow
            v-for="(weekDates, index) in month.rows"
            :key="`weekDate-${index}`"
            class="flex gap-1 mt-1 w-full"
          >
            <RangeCalendarCell
              v-for="weekDate in weekDates"
              :key="weekDate.toString()"
              :date="weekDate"
              class="relative h-7 w-7 p-0 text-center text-xs focus-within:relative focus-within:z-20"
            >
              <RangeCalendarCellTrigger
                :day="weekDate"
                :month="month.value"
                :class="
                  cn(
                    'inline-flex h-7 w-7 items-center justify-center rounded-md text-xs font-normal text-foreground transition-colors cursor-pointer outline-none',
                    'hover:bg-muted',
                    'data-[today]:font-semibold',
                    'data-[selected]:bg-muted data-[selected]:text-foreground',
                    'data-[highlighted]:bg-muted data-[highlighted]:text-foreground',
                    'data-[selection-start]:!bg-foreground data-[selection-start]:!text-background',
                    'data-[selection-end]:!bg-foreground data-[selection-end]:!text-background',
                    'data-[disabled]:text-muted-foreground data-[disabled]:opacity-50',
                    'data-[outside-view]:text-muted-foreground data-[outside-view]:opacity-50'
                  )
                "
              />
            </RangeCalendarCell>
          </RangeCalendarGridRow>
        </RangeCalendarGridBody>
      </RangeCalendarGrid>
    </div>
  </RangeCalendarRoot>
</template>
